import toast, { Toaster } from "react-hot-toast";

import isEmpty from "lodash/isEmpty";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";
import { MdDelete } from "react-icons/md";
import Skeleton from "react-loading-skeleton";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Swal from "sweetalert2";
import { ICONS } from "../../assets/icons";
import Header from "../../components/header";
import SearchInput from "../../components/searchInput";
import { fetcher } from "../../network";
import { API } from "../../network/api";
import { Guide } from "../../types";
import { AddGuidesResponse, RemoveGuidesResponse } from "../../types/responses";
import Drawer from "../home/components/drawer";
import { fetchGuides } from "../../network/globalFetches";

const addGuide = async (name: { name: string }) => {
  const { data } = await fetcher().post<AddGuidesResponse>(API.POST.ADD_GUIDE, {
    name,
  });

  return data;
};

const removeGuideMutation = async (id: string) => {
  const { data } = await fetcher().delete<RemoveGuidesResponse>(
    API.DELETE.REMOVE_GUIDE(id)
  );
  return data;
};

const renderContent = (
  guides: Guide[],
  onDeleteClick: (guide: Guide) => void,
  isLoadingData: boolean,
  isError: boolean
) => {
  if (isError) {
    return (
      <div className="flex-1 flex flex-col w-4/5 mx-auto scrollbar-hide mt-10 items-center">
        <div className="flex flex-col text-center gap-8 h-1/2 w-1/2">
          <h1 className="text-2xl">
            אופס... השרת יצא להפסקת קפה. תן לו כמה דקות ותנסה שוב! ☕
          </h1>
          <img src={ICONS.ERROR_NOT_FOUND} />
        </div>
      </div>
    );
  }

  if (isLoadingData) {
    return (
      <div className="flex-1 overflow-y-auto flex flex-col w-4/5 mx-auto scrollbar-hide mt-10">
        <div className="text-center py-4">
          <Skeleton count={5} height={65} />
        </div>
      </div>
    );
  }

  if (isEmpty(guides)) {
    return (
      <div className="flex flex-col text-center items-center gap-10">
        <h2 className="font-bold text-xl">לא נמצאו מלווים</h2>
        <div className="w-1/4">
          <img
            className="w-full"
            src={ICONS.GUIDE_NOT_FOUND}
            alt="לא נמצאו מלווים"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto flex flex-col w-4/5 mx-auto">
      <div className=" text-center py-4">
        <AnimatePresence>
          {guides.map(({ name, _id, defaultImg }, index) => {
            return (
              <motion.div
                key={_id}
                initial={{ opacity: 0, y: 0 }}
                animate={{ opacity: 1, y: 20 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.3 }}
                layout
                className={`flex justify-between hover:bg-purple-400 hover:cursor-pointer items-center mb-2 p-2 odd:bg-purple-100 border rounded ${
                  index === guides.length - 1 ? "mb-14" : ""
                }`}>
                <div className="flex gap-4 text-right items-center">
                  <div className="rounded-full w-14">
                    <img
                      className="w-full rounded-full"
                      src={defaultImg}
                      alt=""
                    />
                  </div>
                  <p>{name}</p>
                </div>
                <div className="flex items-center gap-14">
                  <button
                    onClick={() => {
                      onDeleteClick({ _id, name, defaultImg: "" });
                    }}>
                    <MdDelete size={22} className="hover:scale-125" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
};

const GuidePage = () => {
  const [filterGuides, setFilterGuides] = useState<Guide[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: fetchGuides.queryKey,
    queryFn: fetchGuides.fetch,
    staleTime: Infinity,
  });

  const { mutate: saveGuide } = useMutation<
    AddGuidesResponse,
    Error,
    { name: string }
  >({
    mutationFn: addGuide,
    onSuccess: ({ savedGuide }) => {
      queryClient.invalidateQueries({ queryKey: fetchGuides.queryKey });
      queryClient.setQueryData<Guide[]>(fetchGuides.queryKey, (oldData) => {
        if (!oldData) return [savedGuide];
        return [savedGuide, ...oldData];
      });
      setFilterGuides((prev) => [savedGuide, ...prev]);
      toast.success(`${savedGuide.name} נוסף בהצלחה`);
    },
    onError: () => {
      toast.error("תקלת מערכת - המלווה לא נשמר לצערנו. נסה שנית מאוחר יותר");
    },
  });

  const { mutate: removeGuide } = useMutation<
    RemoveGuidesResponse,
    Error,
    string
  >({
    mutationFn: removeGuideMutation,
    onSuccess: ({ guideId }) => {
      queryClient.setQueryData<Guide[]>(fetchGuides.queryKey, (oldData) => {
        if (!oldData) return [];
        return oldData.filter((doctor) => doctor._id !== guideId);
      });
      setFilterGuides((prev) => prev.filter((guide) => guide._id !== guideId));
      toast.success("המלווה נמחק בהצלחה");
    },
    onError: () => {
      toast.error("תקלת מערכת - המלווה לא נשמר לצערנו. נסה שנית מאוחר יותר");
    },
  });

  useEffect(() => {
    if (!data) {
      return;
    }
    let filtered = data;

    if (searchTerm) {
      filtered = filtered.filter((patient) =>
        patient.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilterGuides(filtered);
  }, [searchTerm, filterGuides]);

  const onDelete = (guide: Guide) => {
    Swal.fire({
      icon: "warning",
      title: `${guide.name} ימחק לתמיד`,
      showConfirmButton: true,
      confirmButtonText: "מחק",
      confirmButtonColor: "red",
      showCancelButton: true,
      cancelButtonText: "ביטול",
    }).then((isConfirmed) => {
      if (isConfirmed) {
        removeGuide(guide._id);
      }
    });
  };

  useEffect(() => {
    if (data) {
      setFilterGuides(data);
    }
  }, [data]);

  const toggleDrawer = () => {
    setIsDrawerOpen((prev) => !prev);
  };

  return (
    <div className="container mx-auto h-screen flex flex-col">
      <Header>
        <Header.Time />
        <Header.Title title="ניהול מלווים" />
        <Header.Menu toggleDrawer={toggleDrawer} />
      </Header>

      {!isError && (
        <div className="w-1/3 mx-auto mt-6">
          <SearchInput placeholder="חפש מלווה" onSearch={setSearchTerm} />
        </div>
      )}

      <div className="mt-10">
        {!isEmpty(filterGuides) && !isError && (
          <p className="mb-2 w-4/5 mx-auto">
            מספר מלווים: {filterGuides.length}
          </p>
        )}
        {renderContent(filterGuides, onDelete, isLoading, isError)}
      </div>
      <div className="absolute bottom-6 right-6 hover:scale-110 shadow">
        <button
          onClick={() => {
            Swal.fire({
              title: "הזן שם מלווה",
              input: "text",
              inputLabel: "שם המלווה",
              showCancelButton: true,
              cancelButtonText: "ביטול",
              confirmButtonText: "שמור",
              confirmButtonColor: "#C64192",
              inputValidator: (value) => {
                if (!value) {
                  return "שדה חובה";
                }
              },
            }).then(({ isConfirmed, value }) => {
              if (isConfirmed) {
                saveGuide(value);
              }
            });
          }}
          className="bg-[#144082] text-white rounded py-3 px-8 ">
          הוסף מלווה
        </button>
      </div>
      {isDrawerOpen && <Drawer toggleDrawer={toggleDrawer} />}
      <Toaster position="bottom-center" />
    </div>
  );
};

export default GuidePage;
